import Footer from "@/components/common/layout/Footer/Footer";
import { Header } from "@/components";
import { Searchbar } from "@/components/common/ui/Searchbar";
import AnimatedSection from "../../components/common/animations/AnimatedSection";
import ParallaxSection from "../../components/common/animations/ParallaxSection";
import Floating3D from "../../components/common/animations/Floating3D";
import { useEffect, useState, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { motion } from "framer-motion";
import SEO from "@/components/common/seo/seo";
import { searchDomains, DomainSearchResult } from "@/services/dreamscapeApi";
import { Loader2, AlertCircle } from "lucide-react";

if (typeof window !== 'undefined') {
  gsap.registerPlugin(ScrollTrigger);
}

export default function Search() {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const searchQuery = searchParams.get("q") || "";
    
    const [searchInput, setSearchInput] = useState(searchQuery);
    const [searchResults, setSearchResults] = useState<DomainSearchResult[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [hasSearched, setHasSearched] = useState(false);

    const performSearch = useCallback(async (query: string) => {
        if (!query.trim()) {
            setSearchResults([]);
            setHasSearched(false);
            return;
        }

        setLoading(true);
        setError(null);
        setHasSearched(true);

        try {
            // Extract base domain
            const trimmed = query.trim().toLowerCase().replace(/^https?:\/\//, '').replace(/^www\./, '');
            const parts = trimmed.split('.');
            const base = parts[0];

            if (!base || base.length < 2) {
                setError("Please enter a valid domain name or search term");
                setLoading(false);
                return;
            }

            // Search for exact domain and suggestions
            const commonTlds = ['com', 'net', 'org', 'io', 'co', 'info', 'biz', 'xyz', 'app', 'dev', 'tech', 'online', 'au', 'com.au', 'net.au', 'org.au', 'co.uk', 'sydney', 'melbourne'];
            const domainsToSearch = [trimmed, ...commonTlds.map(tld => `${base}.${tld}`)];

            const response = await searchDomains(base, [], domainsToSearch);

            if (response.success && response.results) {
                // Get all results (excluding exact match if it exists)
                const results = response.results.slice(0, 10);
                setSearchResults(results);
            } else {
                setError(response.message || "Failed to search domains");
                setSearchResults([]);
            }
        } catch (err) {
            setError(err instanceof Error ? err.message : "An error occurred");
            setSearchResults([]);
        } finally {
            setLoading(false);
        }
    }, []);

    // Perform search when query parameter changes
    useEffect(() => {
        setSearchInput(searchQuery);
        if (searchQuery) {
            performSearch(searchQuery);
        } else {
            setSearchResults([]);
            setHasSearched(false);
        }
    }, [searchQuery, performSearch]);

    const handleSearch = (query: string) => {
        if (query.trim()) {
            navigate(`/search?q=${encodeURIComponent(query.trim())}`);
        }
    };

    // Animation effects - only run once on mount
    useEffect(() => {
        // Animate search bar
        gsap.fromTo('.search-bar-animate',
            {
                scale: 0.9,
                opacity: 0,
                y: -30
            },
            {
                scale: 1,
                opacity: 1,
                y: 0,
                duration: 0.8,
                ease: "elastic.out(1, 0.5)",
                delay: 0.2
            }
        );

        // Floating animation for title
        gsap.to('.search-title', {
            y: -10,
            duration: 3,
            repeat: -1,
            yoyo: true,
            ease: "sine.inOut"
        });
    }, []);

    // Animation for search results cards - only when results change
    useEffect(() => {
        if (searchResults.length === 0) return;

        // Kill existing scroll triggers for search cards
        ScrollTrigger.getAll().forEach(trigger => {
            const triggerElement = trigger.vars?.trigger;
            if (triggerElement && typeof triggerElement !== 'string') {
                const element = triggerElement as Element;
                if (element.classList?.contains('search-card-item')) {
                    trigger.kill();
                }
            }
        });

        // Stagger animation for search cards
        const cards = document.querySelectorAll('.search-card-item');
        
        cards.forEach((card, index) => {
            gsap.fromTo(card,
                {
                    opacity: 0,
                    y: 50,
                    scale: 0.9
                },
                {
                    opacity: 1,
                    y: 0,
                    scale: 1,
                    duration: 0.6,
                    delay: index * 0.1,
                    ease: "back.out(1.2)",
                    scrollTrigger: {
                        trigger: card,
                        start: "top 85%",
                        toggleActions: "play none none none"
                    }
                }
            );
        });

        return () => {
            ScrollTrigger.getAll().forEach(trigger => {
                const triggerElement = trigger.vars?.trigger;
                if (triggerElement && typeof triggerElement !== 'string') {
                    const element = triggerElement as Element;
                    if (element.classList?.contains('search-card-item')) {
                        trigger.kill();
                    }
                }
            });
        };
    }, [searchResults]);

    return (
        <>
            <SEO
                title="Search - SAN micron"
                description="Find relevant articles, resources, and information quickly with SAN micron's comprehensive search feature."
            />
            <div className="text-white relative overflow-hidden">
                <Header />
                
                <ParallaxSection speed={0.2}>
                    <div className="container">
                        <AnimatedSection delay={0.1} direction="down">
                            <div className="max-w-3xl mx-auto pt-32 pb-32">
                                <Floating3D intensity={15}>
                                    <motion.h1 
                                        className="search-title text-xl sm:text-3xl md:text-5xl leading-tight font-semibold mb-12 bg-gradient-to-r from-purple-400 via-pink-400 to-blue-400 bg-clip-text text-transparent"
                                        initial={{ opacity: 0, y: 20 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        transition={{ duration: 0.6 }}
                                    >
                                        Search results
                                    </motion.h1>
                                </Floating3D>
                                
                                <div className="search-bar-animate">
                                    <motion.div
                                        whileHover={{ scale: 1.02 }}
                                        transition={{ type: "spring", stiffness: 300 }}
                                    >
                                        <Searchbar 
                                            onSearch={handleSearch}
                                            value={searchInput}
                                            onChange={setSearchInput}
                                            placeholder="Search domains, services, or content..."
                                        />
                                    </motion.div>
                                </div>
                                
                                <AnimatedSection delay={0.3} direction="left">
                                    <motion.span 
                                        className="block mt-4 text-gray-300"
                                        initial={{ opacity: 0 }}
                                        animate={{ opacity: 1 }}
                                        transition={{ delay: 0.5 }}
                                    >
                                        {loading ? (
                                            <span className="flex items-center gap-2">
                                                <Loader2 className="w-4 h-4 animate-spin" />
                                                Searching...
                                            </span>
                                        ) : hasSearched ? (
                                            `Results: ${searchResults.length} ${searchResults.length === 1 ? 'item' : 'items'}`
                                        ) : (
                                            "Enter a search query to get started"
                                        )}
                                    </motion.span>
                                </AnimatedSection>
                            </div>
                        </AnimatedSection>
                    </div>
                </ParallaxSection>

                <div className="bg-white relative">
                    <div className="container text-black py-12">
                        {error && (
                            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-600">
                                <AlertCircle className="w-5 h-5 flex-shrink-0" />
                                <span>{error}</span>
                            </div>
                        )}

                        {loading ? (
                            <div className="flex flex-col items-center justify-center py-16">
                                <Loader2 className="w-12 h-12 animate-spin text-primary mb-4" />
                                <p className="text-gray-600">Searching...</p>
                            </div>
                        ) : searchResults.length > 0 ? (
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                {searchResults.map((result, index) => (
                                    <motion.div
                                        key={`${result.domain}-${index}`}
                                        className="search-card-item"
                                        whileHover={{ 
                                            scale: 1.03, 
                                            rotateY: 5,
                                            z: 20
                                        }}
                                        transition={{ 
                                            type: "spring", 
                                            stiffness: 300 
                                        }}
                                    >
                                        <Floating3D intensity={12}>
                                            <div className="card bg-[#F7F7F7] p-6 rounded-lg">
                                                <span className={`bg-blue-100 text-blue text-sm px-3 py-1 font-semibold rounded-full ${
                                                    result.available ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                                                }`}>
                                                    {result.available ? 'Available' : 'Unavailable'}
                                                </span>
                                                <h2 className="mt-2 mb-2 leading-tight font-bold text-[20px] min-h-[52px]">
                                                    {result.domain}
                                                </h2>
                                                <p className="overflow-hidden text-ellipsis line-clamp-2-custom text-gray-600">
                                                    {result.available ? (
                                                        <>
                                                            Registration Price: {result.currency} ${result.registerPrice?.toFixed(2) || 'N/A'}
                                                            {result.renewPrice && (
                                                                <> • Renewal: {result.currency} ${result.renewPrice.toFixed(2)}</>
                                                            )}
                                                        </>
                                                    ) : (
                                                        result.status || 'This domain is not available for registration'
                                                    )}
                                                </p>
                                            </div>
                                        </Floating3D>
                                    </motion.div>
                                ))}
                            </div>
                        ) : hasSearched && !loading ? (
                            <div className="text-center py-16">
                                <p className="text-gray-600 text-lg">No results found for "{searchQuery}"</p>
                                <p className="text-gray-300 text-sm mt-2">Try a different search term</p>
                            </div>
                        ) : (
                            <div className="text-center py-16">
                                <p className="text-gray-600 text-lg">Start searching to see results</p>
                            </div>
                        )}
                    </div>
                </div>

                <AnimatedSection delay={0.8}>
                    <Footer />
                </AnimatedSection>
            </div>
        </>
    );
}
